import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  Warning,
  ContentCopy,
  LinkOff,
  MergeType,
  CheckCircle,
  Block,
} from '@mui/icons-material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';

// Mock data for Data Quality Dashboard

const mockInactiveItems = [
  { id: 1, itemNumber: 'FLR-003', description: 'Wholemeal Flour (Discontinued)', status: 'Inactive', linkedRecipes: ['Wholemeal Bread', 'Brown Loaf'], lastUsed: '2025-09-15' },
  { id: 2, itemNumber: 'CHZ-002', description: 'Cheddar Mild (Delisted)', status: 'Inactive', linkedRecipes: ['Cheese Toastie', 'Ploughman\'s Sandwich'], lastUsed: '2025-11-01' },
  { id: 3, itemNumber: 'HRB-004', description: 'Dried Oregano (Old Supplier)', status: 'Inactive', linkedRecipes: ['Pizza Margherita', 'House Tomato Sauce'], lastUsed: '2026-01-10' },
];

const mockDuplicates = [
  { id: 1, items: [{ itemNumber: 'TOM-001', description: 'Chopped Tomatoes (Tinned)' }, { itemNumber: 'TOM-003', description: 'Chopped Tomatoes (Canned)' }], matchType: 'Description', confidence: 0.92 },
  { id: 2, items: [{ itemNumber: 'OLV-001', description: 'Extra Virgin Olive Oil' }, { itemNumber: 'OLV-002', description: 'EVOO Premium' }], matchType: 'Supplier SKU', confidence: 0.85 },
  { id: 3, items: [{ itemNumber: 'BTR-001', description: 'Unsalted Butter' }, { itemNumber: 'BTR-003', description: 'Butter Unsalted Block' }], matchType: 'GTIN', confidence: 0.98 },
];

const mockOrphans = [
  { id: 1, itemNumber: 'SPC-009', description: 'Saffron Threads', lastOrdered: '2025-06-01', costPrice: 45.00 },
  { id: 2, itemNumber: 'NUT-012', description: 'Macadamia Nuts (Whole)', lastOrdered: '2025-08-20', costPrice: 32.00 },
  { id: 3, itemNumber: 'VEG-018', description: 'Purple Sprouting Broccoli', lastOrdered: '2026-01-15', costPrice: 8.50 },
  { id: 4, itemNumber: 'FRT-007', description: 'Passion Fruit (Case)', lastOrdered: '2025-12-01', costPrice: 22.00 },
];

const mockConsolidation = [
  { id: 1, inventorySku: 'TOM-001', omContentRecords: 3, suppliers: ['FreshCo', 'Brakes', 'Local Farm'], action: null as string | null },
  { id: 2, inventorySku: 'OLV-001', omContentRecords: 2, suppliers: ['Mediterranean Oils', 'Bulk Oils Ltd'], action: null as string | null },
];

type DqTab = 'inactive' | 'duplicates' | 'orphans' | 'consolidation';

const DataQualityDashboard = () => {
  const { t } = useTranslation('recipeV2');
  const [activeTab, setActiveTab] = useState<DqTab>('inactive');

  const summaryCards = [
    { key: 'inactive', icon: <Warning color="warning" />, label: 'Inactive in Active Recipes', count: mockInactiveItems.length, color: 'warning.main' },
    { key: 'duplicates', icon: <ContentCopy color="error" />, label: 'Potential Duplicates', count: mockDuplicates.length, color: 'error.main' },
    { key: 'orphans', icon: <LinkOff color="info" />, label: 'Orphan Items', count: mockOrphans.length, color: 'info.main' },
    { key: 'consolidation', icon: <MergeType color="secondary" />, label: 'Consolidation Candidates', count: mockConsolidation.length, color: 'secondary.main' },
  ];

  return (
    <PageArea>
      <Stack spacing={3}
        width="100%">
        <PageTitle subTitle="Identify and resolve data quality issues across recipes and stock items">
          Data Quality
        </PageTitle>

        {/* Summary cards */}
        <Grid container
          spacing={2}>
          {summaryCards.map((card) => (
            <Grid item
              xs={12}
              sm={6}
              md={3}
              key={card.key}>
              <Card variant="outlined"
                sx={{
                  cursor: 'pointer',
                  borderColor: activeTab === card.key ? card.color : 'divider',
                  borderWidth: activeTab === card.key ? 2 : 1,
                }}
                onClick={() => setActiveTab(card.key as DqTab)}>
                <CardContent>
                  <Stack direction="row"
                    alignItems="center"
                    spacing={1}>
                    {card.icon}
                    <Box>
                      <Typography variant="textXS"
                        color="text.secondary">{card.label}</Typography>
                      <Typography variant="displayXS"
                        fontWeight={700}>{card.count}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Tab content */}
        <Tabs value={activeTab}
          onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Inactive Items"
            value="inactive" />
          <Tab label="Duplicates"
            value="duplicates" />
          <Tab label="Orphan Items"
            value="orphans" />
          <Tab label="Consolidation"
            value="consolidation" />
        </Tabs>

        {/* Inactive Items */}
        {activeTab === 'inactive' && (
          <Paper variant="outlined"
            sx={{ p: 2 }}>
            <Typography variant="textLG"
              fontWeight={600}
              gutterBottom>
              Inactive items linked to active recipes
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item Number</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Linked Recipes</TableCell>
                    <TableCell>Last Used</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockInactiveItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography fontFamily="monospace"
                          variant="textSM">{item.itemNumber}</Typography>
                      </TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>
                        <Chip label={item.status}
                          size="small"
                          color="error" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row"
                          spacing={0.5}
                          flexWrap="wrap">
                          {item.linkedRecipes.map((r) => (
                            <Chip key={r}
                              label={r}
                              size="small"
                              variant="outlined" />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>{item.lastUsed}</TableCell>
                      <TableCell>
                        <Button size="md"
                          variant="secondary">Replace</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Duplicates */}
        {activeTab === 'duplicates' && (
          <Paper variant="outlined"
            sx={{ p: 2 }}>
            <Typography variant="textLG"
              fontWeight={600}
              gutterBottom>
              Similar items that may be duplicates
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Items</TableCell>
                    <TableCell>Match Type</TableCell>
                    <TableCell>Confidence</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockDuplicates.map((dup) => (
                    <TableRow key={dup.id}>
                      <TableCell>
                        <Stack spacing={0.5}>
                          {dup.items.map((item) => (
                            <Typography key={item.itemNumber}
                              variant="textSM">
                              <Typography component="span"
                                fontFamily="monospace"
                                variant="textXS">{item.itemNumber}</Typography>
                              {' '}{item.description}
                            </Typography>
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip label={dup.matchType}
                          size="small"
                          variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography color={dup.confidence > 0.9 ? 'error.main' : 'warning.main'}
                          fontWeight={600}>
                          {(dup.confidence * 100).toFixed(0)}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row"
                          spacing={0.5}>
                          <Button size="md"
                            variant="primary"
                            startIcon={<MergeType />}>Merge</Button>
                          <Button size="md"
                            variant="secondary"
                            startIcon={<Block />}>Ignore</Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Orphans */}
        {activeTab === 'orphans' && (
          <Paper variant="outlined"
            sx={{ p: 2 }}>
            <Typography variant="textLG"
              fontWeight={600}
              gutterBottom>
              Items not used in any recipe or recent order
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item Number</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Cost Price</TableCell>
                    <TableCell>Last Ordered</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockOrphans.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography fontFamily="monospace"
                          variant="textSM">{item.itemNumber}</Typography>
                      </TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell align="right">£{item.costPrice.toFixed(2)}</TableCell>
                      <TableCell>{item.lastOrdered}</TableCell>
                      <TableCell>
                        <Stack direction="row"
                          spacing={0.5}>
                          <Button size="md"
                            variant="secondary"
                            startIcon={<Block />}>Deactivate</Button>
                          <Button size="md"
                            variant="secondary">Flag for Review</Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Consolidation */}
        {activeTab === 'consolidation' && (
          <Paper variant="outlined"
            sx={{ p: 2 }}>
            <Typography variant="textLG"
              fontWeight={600}
              gutterBottom>
              Multiple OM Content records mapping to same InventorySKU
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Inventory SKU</TableCell>
                    <TableCell align="center">OM Content Records</TableCell>
                    <TableCell>Suppliers</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockConsolidation.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography fontFamily="monospace"
                          variant="textSM">{item.inventorySku}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={item.omContentRecords}
                          size="small"
                          color={item.omContentRecords > 2 ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row"
                          spacing={0.5}
                          flexWrap="wrap">
                          {item.suppliers.map((s) => (
                            <Chip key={s}
                              label={s}
                              size="small"
                              variant="outlined" />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row"
                          spacing={0.5}>
                          <Button size="md"
                            variant="primary"
                            startIcon={<MergeType />}>Consolidate</Button>
                          <Button size="md"
                            variant="secondary"
                            startIcon={<CheckCircle />}>OK</Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Stack>
    </PageArea>
  );
};

export default DataQualityDashboard;
